import type { Schema } from "@focus/formComponents";

function resolveRef(schema: any, rootSchema: any) {
  if (!schema.$ref) return schema;

  const path = schema.$ref.replace("#/", "").split("/");
  let resolved = rootSchema;
  for (const key of path) {
    resolved = resolved[key];
  }
  return resolved;
}

/**
 * Builds a new row (`ITableData`) based on the provided JSON schema and optional uiSchema.
 *
 * - Uses `uiSchema` to determine field behavior when available.
 * - Falls back to `schema` types when `uiSchema` is missing.
 * - Applies default values defined in the schema when present.
 * - Ensures a fully initialized row object.
 */
export function buildNewConfig(schema: Schema, rootSchema = schema) {
  const newRow: Record<string, string> = {};

  const properties = schema.items?.properties ?? schema.properties ?? {};

  for (const fieldName in properties) {
    if (!fieldName || fieldName === "undefined") continue;
    const fieldSchemaRaw = properties[fieldName];
    const fieldSchema = resolveRef(fieldSchemaRaw, rootSchema);

    let value: any;

    // Helper: default fallback
    const getDefault = () =>
      fieldSchema.default !== undefined ? fieldSchema.default : undefined;

    switch (fieldSchema.type) {
      case "string":
        if (fieldSchema.enum) {
          value = getDefault() ?? fieldSchema.enum[0];
          break;
        }
        value = getDefault() ?? "";
        break;
      case "number":
      case "integer":
        value = getDefault() ?? 0;
        break;
      case "boolean":
        value = getDefault() ?? false;
        break;
      case "array":
        value = getDefault() ?? [];
        break;
      case "object":
        value = buildNewConfig(fieldSchema, rootSchema);
        break;
      default:
        value = getDefault();
    }

    if (!fieldName || fieldName === "undefined") continue;
    newRow[fieldName] = value;
  }
  return newRow;
}

type Role = "admin" | "org-admin" | "user";

export function transformSchemaByRole(schema: any, role: Role): any {
  // Clon profundo para no mutar el original
  const cloned = structuredClone(schema);

  function traverse(node: any, parentReadOnly = false): any {
    if (!node || typeof node !== "object") return node;

    let currentReadOnly: boolean;

    if (role === "admin") {
      // admin siempre gana → nada readonly
      currentReadOnly = false;
    } else {
      // user u otros roles → lógica heredada
      currentReadOnly = parentReadOnly || node.readOnly === true;
    }

    // Aplicar al nodo actual
    node.readOnly = currentReadOnly;

    // Caso: objeto con properties
    if (node.properties && typeof node.properties === "object") {
      const newProps: Record<string, any> = {};
      const removedKeys = new Set<string>();

      for (const [key, prop] of Object.entries(node.properties)) {
        const p = prop as any;

        // ---- REGLA ui:hidden ----
        if (p["ui:hidden"]) {
          if (role === "admin") {
            delete p["ui:hidden"];
          } else {
            // eliminar propiedad del required
            removedKeys.add(key);
            continue;
          }
        }

        newProps[key] = traverse(p, currentReadOnly);
      }

      node.properties = newProps;

      // ---- LIMPIAR required ----
      if (Array.isArray(node.required)) {
        node.required = node.required.filter(
          (key: string) => !removedKeys.has(key),
        );
      }

      // ---- LIMPIAR ui:order ----
      if (Array.isArray(node["ui:order"])) {
        node["ui:order"] = node["ui:order"].filter(
          (key: string) => !removedKeys.has(key),
        );
      }
    }

    // Caso: arrays
    if (node.items) {
      if (Array.isArray(node.items)) {
        node.items = node.items.map((item) => traverse(item, currentReadOnly));
      } else {
        node.items = traverse(node.items, currentReadOnly);
      }
    }

    // Caso: definitions
    if (node.definitions) {
      for (const key of Object.keys(node.definitions)) {
        node.definitions[key] = traverse(
          node.definitions[key],
          currentReadOnly,
        );
      }
    }

    return node;
  }

  return traverse(cloned);
}

export function deepEqual(a: any, b: any): boolean {
  if (a === b) return true;

  if (typeof a !== typeof b) return false;

  if (a && b && typeof a === "object") {
    if (Array.isArray(a)) {
      if (!Array.isArray(b) || a.length !== b.length) return false;
      return a.every((val, i) => deepEqual(val, b[i]));
    }

    const keysA = Object.keys(a);
    const keysB = Object.keys(b);

    if (keysA.length !== keysB.length) return false;

    return keysA.every((key) => deepEqual(a[key], b[key]));
  }

  return false;
}
